import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useBrand } from '@/hooks/useBrand';
import { fetchMissions, MISSION_PLATFORMS, type Mission } from '@/lib/missions';

// ─────────────────────────────────────────────────────────────
// Page publique « Campagnes créateurs » (/missions-campagnes).
// Objectif : attirer des créateurs externes (TikTok, Instagram…)
// et référencer la marketplace de missions sur les moteurs de recherche.
// Accessible sans compte — la candidature demande simplement une connexion.
// ─────────────────────────────────────────────────────────────

const STEPS = [
  { icon: 'ri-user-add-line', title: 'Créez votre compte créateur', text: 'Gratuit et en 1 minute. Renseignez vos réseaux et votre nombre d’abonnés.' },
  { icon: 'ri-compass-3-line', title: 'Choisissez une campagne', text: 'Filtrez par réseau, par pays ou par budget. Chaque campagne indique son tarif pour 1000 vues.' },
  { icon: 'ri-send-plane-line', title: 'Postulez en un clic', text: 'Vos liens de réseaux sont pré-remplis depuis votre profil. Le commerce reçoit votre candidature.' },
  { icon: 'ri-money-dollar-circle-line', title: 'Publiez, vous êtes payé', text: 'Une fois retenu, publiez le contenu, ajoutez le lien, et le montant est calculé selon les vues validées.' },
];

const FAQ_ITEMS = [
  {
    q: 'Faut-il un minimum d’abonnés pour participer ?',
    a: 'Non. Chaque commerce définit lui-même ses critères. Certaines campagnes ciblent de petits créateurs locaux, d’autres privilégient une très large audience.',
  },
  {
    q: 'Comment suis-je payé ?',
    a: 'Le tarif est exprimé pour 1000 vues. Une fois votre publication vérifiée, le montant dû est calculé automatiquement (vues retenues ÷ 1000 × tarif) et réglé par le commerce via le portefeuille Zifek.',
  },
  {
    q: 'Puis-je utiliser la musique d’un artiste ?',
    a: 'C’est le propriétaire de la campagne qui l’autorise ou non. L’information est affichée sur la fiche de chaque mission.',
  },
  {
    q: 'Quels réseaux sont acceptés ?',
    a: 'TikTok, Instagram, Facebook, YouTube et X (Twitter). Une campagne peut demander plusieurs réseaux à la fois.',
  },
];

function fmtMoney(value: number | null, devise: string): string {
  const n = Number(value) || 0;
  return `${n.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${devise}`;
}

function fmtDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export default function MissionsCampagnesPage() {
  const { user } = useAuth();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Un membre connecté ne doit jamais sortir de son espace : les campagnes
  // se consultent et se postulent depuis le tableau de bord (même navigation).
  useEffect(() => {
    if (user) navigate('/mon-compte/missions', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    document.title = `Campagnes rémunérées pour créateurs TikTok & Instagram | ${brand.name}`;
    const meta = document.querySelector('meta[name="description"]');
    const content = 'Postulez à des campagnes rémunérées de commerces : TikTok, Instagram, Facebook, YouTube. Tarif par 1000 vues, paiement après validation des vues.';
    if (meta) meta.setAttribute('content', content);
  }, [brand.name]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const rows = await fetchMissions();
        if (active) setMissions(rows.filter((m) => m.statut === 'ouverte'));
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : 'Impossible de charger les campagnes.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const platformsAvailable = useMemo(
    () => MISSION_PLATFORMS.filter((p) => missions.some((m) => (m.plateformes || []).includes(p.key))),
    [missions],
  );

  const registerHref = '/register-client';
  const spaceHref = user ? '/mon-compte/missions' : '/login-client';

  return (
    <div className="min-h-screen bg-background-50">
      {/* En-tête */}
      <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-14 md:h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 min-w-0 no-underline">
            <span className="w-8 h-8 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center font-bold text-sm font-heading flex-shrink-0">
              {(brand.name || 'Z').charAt(0)}
            </span>
            <span className="text-sm md:text-base font-bold font-heading text-foreground-950 truncate">{brand.name}</span>
          </Link>
          <div className="flex items-center gap-1.5">
            <Link to="/" className="hidden sm:inline-flex px-3 py-2 rounded-full text-xs font-medium text-foreground-600 hover:bg-background-100 no-underline whitespace-nowrap">
              Accueil
            </Link>
            {user ? (
              <Link to={spaceHref} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap no-underline hover:bg-primary-600">
                Mes missions
              </Link>
            ) : (
              <>
                <Link to="/login-client" className="px-3 py-2 rounded-full text-xs font-medium text-foreground-600 hover:bg-background-100 no-underline whitespace-nowrap">
                  Connexion
                </Link>
                <Link to={registerHref} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap no-underline hover:bg-primary-600">
                  Devenir créateur
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Young%20african%20content%20creator%20filming%20a%20short%20video%20with%20a%20smartphone%20on%20a%20tripod%2C%20warm%20sunset%20light%2C%20modern%20minimal%20studio%20with%20terracotta%20and%20cream%20tones%2C%20editorial%20photography%2C%20soft%20shadows%2C%20high%20detail&width=1600&height=900&seq=missions-creators-hero-2026&orientation=landscape"
            alt="Créateur de contenu filmant une vidéo avec son téléphone"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/80 via-foreground-950/65 to-foreground-950/85"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50/15 text-background-50 text-[11px] font-semibold backdrop-blur-sm border border-background-50/20">
            <i className="ri-fire-line"></i>
            Marketplace de campagnes rémunérées
          </span>
          <h1 className="mt-4 text-3xl md:text-5xl font-bold font-heading text-background-50 leading-tight max-w-3xl">
            Transformez vos vues en revenus
          </h1>
          <p className="mt-4 text-sm md:text-base text-background-50/80 max-w-2xl leading-relaxed">
            Des commerces publient des missions : publiez leur contenu sur vos réseaux et touchez un tarif pour
            chaque 1000 vues. TikTok, Instagram, Facebook, YouTube et X — sans minimum d&apos;abonnés imposé.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link
              to={registerHref}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap no-underline hover:bg-primary-600"
            >
              <i className="ri-rocket-line"></i>
              Créer mon compte créateur
            </Link>
            <a
              href="#campagnes"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-background-50/10 text-background-50 text-sm font-semibold whitespace-nowrap no-underline border border-background-50/30 hover:bg-background-50/20"
            >
              <i className="ri-compass-3-line"></i>
              Voir les campagnes
            </a>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-background-50/70">
            <span className="inline-flex items-center gap-1.5"><i className="ri-check-double-line text-accent-300"></i>Inscription gratuite</span>
            <span className="inline-flex items-center gap-1.5"><i className="ri-check-double-line text-accent-300"></i>Paiement après validation des vues</span>
            <span className="inline-flex items-center gap-1.5"><i className="ri-check-double-line text-accent-300"></i>{missions.length} campagne{missions.length > 1 ? 's' : ''} en cours</span>
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-950">Comment ça marche</h2>
        <p className="mt-2 text-sm text-foreground-600 max-w-2xl">
          Quatre étapes, de l&apos;inscription au paiement. Aucune négociation compliquée : le tarif est affiché dès le départ.
        </p>
        <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((step, index) => (
            <div key={step.title} className="bg-background-100/60 border border-background-200/60 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                  <i className={step.icon}></i>
                </span>
                <span className="text-[11px] font-bold text-foreground-400 tracking-wider">ÉTAPE {index + 1}</span>
              </div>
              <h3 className="text-sm font-bold text-foreground-950">{step.title}</h3>
              <p className="mt-1.5 text-xs text-foreground-600 leading-relaxed">{step.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Campagnes ouvertes */}
      <section id="campagnes" className="bg-background-100/50 border-y border-background-200/60">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-950">Campagnes ouvertes</h2>
              <p className="mt-2 text-sm text-foreground-600">
                Les missions actuellement proposées par les commerces. Postulez avec vos liens de réseaux sociaux.
              </p>
            </div>
            {platformsAvailable.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {platformsAvailable.map((p) => (
                  <span key={p.key} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-50 border border-background-200/70 text-[11px] font-medium text-foreground-600 whitespace-nowrap">
                    <i className={p.icon}></i>
                    {p.label}
                  </span>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="mt-5 flex items-start gap-2 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              <i className="ri-error-warning-line mt-0.5"></i>
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
              <p className="mt-3 text-xs text-foreground-500">Chargement des campagnes…</p>
            </div>
          ) : missions.length === 0 ? (
            <div className="mt-6 bg-background-50 border border-background-200/70 rounded-lg px-6 py-12 text-center">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-megaphone-line text-2xl text-foreground-400"></i>
              </div>
              <h3 className="text-sm font-bold text-foreground-900">Aucune campagne ouverte aujourd&apos;hui</h3>
              <p className="mt-1 text-xs text-foreground-500 max-w-md mx-auto">
                Créez votre compte créateur : vous serez notifié dès qu&apos;une nouvelle mission correspond à vos réseaux.
              </p>
              <Link
                to={registerHref}
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap no-underline hover:bg-primary-600"
              >
                <i className="ri-user-add-line"></i>
                Créer mon compte
              </Link>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {missions.map((mission) => {
                const media = (mission.media || []).filter((m) => m && m.url);
                const cover = media.find((m) => m.type !== 'video') || media[0];
                const devise = mission.monaie || 'MAD';
                return (
                  <article key={mission.id} className="flex flex-col bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                    {cover && cover.type !== 'video' ? (
                      <div className="w-full h-36 bg-background-100 overflow-hidden">
                        <img src={cover.url} alt={mission.titre} className="w-full h-full object-cover object-top" />
                      </div>
                    ) : (
                      <div className="w-full h-24 bg-secondary-50 flex items-center justify-center">
                        <i className="ri-megaphone-line text-2xl text-secondary-400"></i>
                      </div>
                    )}
                    <div className="flex flex-col flex-1 p-4">
                      <h3 className="text-sm font-bold font-heading text-foreground-950 line-clamp-2 leading-snug">{mission.titre}</h3>
                      <p className="mt-1 text-[11px] text-foreground-500 truncate">par {mission.auteur_nom || 'un commerce Zifek'}</p>
                      {mission.description && (
                        <p className="mt-2 text-xs text-foreground-600 leading-relaxed line-clamp-2">{mission.description}</p>
                      )}
                      <div className="mt-3 flex flex-wrap items-center gap-1.5">
                        {(mission.plateformes || []).map((key) => {
                          const meta = MISSION_PLATFORMS.find((p) => p.key === key);
                          return (
                            <span key={key} className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[10px] font-medium whitespace-nowrap">
                              <i className={`${meta?.icon || 'ri-share-line'} mr-1`}></i>
                              {meta?.label || key}
                            </span>
                          );
                        })}
                        {mission.pays === 'International' ? (
                          <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 text-[10px] font-semibold whitespace-nowrap">🌍 International</span>
                        ) : mission.ville ? (
                          <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[10px] font-medium whitespace-nowrap">
                            <i className="ri-map-pin-line mr-1"></i>
                            {mission.ville}
                          </span>
                        ) : mission.pays ? (
                          <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[10px] font-medium whitespace-nowrap">
                            <i className="ri-global-line mr-1"></i>
                            {mission.pays}
                          </span>
                        ) : null}
                        {mission.date_limite && (
                          <span className="px-2 py-0.5 rounded-full bg-accent-50 text-accent-800 text-[10px] font-medium whitespace-nowrap">
                            <i className="ri-calendar-line mr-1"></i>
                            {fmtDate(mission.date_limite)}
                          </span>
                        )}
                      </div>
                      <div className="mt-auto pt-3 flex items-end justify-between gap-2 border-t border-background-200/60">
                        <div>
                          <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Tarif</p>
                          <p className="text-sm font-bold text-foreground-950 whitespace-nowrap">
                            {fmtMoney(mission.tarif_1000_vues, devise)}
                            <span className="text-[10px] font-medium text-foreground-500"> / 1000 vues</span>
                          </p>
                        </div>
                        <Link
                          to={user ? spaceHref : `/login-client?redirect=${encodeURIComponent('/mon-compte/missions')}`}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap no-underline hover:bg-primary-600"
                        >
                          <i className="ri-send-plane-line"></i>
                          Postuler
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-950">Questions fréquentes</h2>
        <div className="mt-6 divide-y divide-background-200/70 border-y border-background-200/70">
          {FAQ_ITEMS.map((item) => (
            <article key={item.q} className="py-4">
              <h3 className="text-sm font-semibold text-foreground-900 flex items-start gap-2">
                <i className="ri-question-line text-primary-500 mt-0.5"></i>
                {item.q}
              </h3>
              <p className="mt-1.5 text-xs text-foreground-600 leading-relaxed pl-6">{item.a}</p>
            </article>
          ))}
        </div>
      </section>

      {/* CTA final */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 pb-14">
        <div className="rounded-xl bg-foreground-950 px-6 md:px-10 py-9 md:py-12 flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-background-50">
              Prêt à monétiser votre audience ?
            </h2>
            <p className="mt-2 text-sm text-background-50/70 max-w-xl leading-relaxed">
              Créez votre compte créateur, ajoutez vos réseaux et postulez aux campagnes qui vous correspondent.
            </p>
          </div>
          <Link
            to={registerHref}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap no-underline hover:bg-accent-600"
          >
            <i className="ri-user-add-line"></i>
            Créer mon compte créateur
          </Link>
        </div>
      </section>

      <footer className="border-t border-background-200/70 py-6">
        <div className="max-w-6xl mx-auto px-4 md:px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-foreground-500">© {new Date().getFullYear()} {brand.name} — Campagnes créateurs.</p>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xs text-foreground-500 hover:text-foreground-800 no-underline">Accueil</Link>
            <Link to="/blog" className="text-xs text-foreground-500 hover:text-foreground-800 no-underline">Blog</Link>
            <Link to="/conditions-utilisation" className="text-xs text-foreground-500 hover:text-foreground-800 no-underline">Conditions</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}